import { useCallback, useEffect, useState } from "react";
import { useAtom, useAtomValue } from "jotai";
import { userIdAtom } from "@/atoms/authAtoms";
import { atomWithStorage } from "jotai/utils";
import { useSearchParams } from "react-router-dom";
import {
  parseModeParam,
  parseRoundParam,
  reviewTask,
  type PanelMode,
  type ReviewProgressResponse,
  type RailRound,
  type ReviewTask,
} from "@/types/devicePanelReview";

/** Where a link or a step opened the page; meaningless once the mode flips. */
const STEPPING_PARAMS = ["at", "finding", "start"];

/** Each signed-in user's last mode per panel on this device (a per-viewer
 * convenience): one user's choice never carries over to the next on a shared
 * browser. */
const panelModesAtom = atomWithStorage<Record<string, Record<string, PanelMode>>>(
  "devicePanelModesByUser",
  {},
);

/** The caller's remembered mode on a panel (View until they pick one) and a
 * setter that remembers a new one. */
export function useRememberedPanelMode(
  panelId: string,
): [PanelMode | undefined, (mode: PanelMode) => void] {
  const user = useAtomValue(userIdAtom);
  const [modesByUser, setModesByUser] = useAtom(panelModesAtom);
  const remembered = (user && modesByUser[user]?.[panelId]) || undefined;
  const remember = useCallback(
    (mode: PanelMode) => {
      if (user && mode !== remembered)
        setModesByUser((current) => ({
          ...current,
          [user]: { ...current[user], [panelId]: mode },
        }));
    },
    [user, remembered, panelId, setModesByUser],
  );
  return [remembered, remember];
}

/**
 * The caller's review on a panel page, from the link, the panel's remembered
 * mode and each round's progress:
 * - `mode`: `?mode=` when the link names one (then remembered for the panel),
 *   else the remembered mode, else View; `startsReview` (an entry link) turns
 *   Review on regardless.
 * - `task`: the caller's review task (null without one), on `?round=` when named.
 * - `round`: the round under review while Review is on for a caller with a
 *   task; null in View.
 * - `unknown`: what the link names that is not known, if anything.
 * Once reviewing, the round is pinned (in the URL when the switch or a review
 * link sets it, else for the page's life), so finishing one round does not move
 * the reader into the next mid-session; flipping the switch picks it afresh.
 */
export function usePanelReview(
  panelId: string,
  progress: Partial<Record<RailRound, ReviewProgressResponse>>,
  startsReview = false,
) {
  const [params, setParams] = useSearchParams();
  const [remembered, remember] = useRememberedPanelMode(panelId);
  const modeValue = params.get("mode");
  const linked = modeValue === null ? undefined : parseModeParam(modeValue);
  const mode: PanelMode | null = linked === undefined ? (remembered ?? "view") : linked;
  const roundValue = params.get("round");
  // A round picked without `?round=` stays for the page's life.
  const [pinned, setPinned] = useState<RailRound | null>(null);
  const requested = roundValue === null ? pinned : parseRoundParam(roundValue);
  const task = reviewTask(progress, requested);
  const round = (mode === "review" || startsReview) && task ? task.round : null;
  // The URL is the one writer: whatever mode it names is remembered.
  useEffect(() => {
    if (linked) remember(linked);
  }, [linked, remember]);
  useEffect(() => {
    if (round && roundValue === null && pinned !== round) setPinned(round);
  }, [round, roundValue, pinned]);
  // One URL write per flip: the mode (remembered from there), and in Review the
  // round picked afresh. A flip ends whatever step or link the page was opened
  // on, so those go too.
  const setMode = (next: PanelMode) => {
    const fresh = next === "review" ? reviewTask(progress, null) : null;
    setPinned(null);
    setParams(
      (current) => {
        const updated = new URLSearchParams(current);
        for (const stepping of STEPPING_PARAMS) updated.delete(stepping);
        updated.set("mode", next);
        if (fresh) updated.set("round", fresh.round);
        else updated.delete("round");
        return updated;
      },
      { replace: true },
    );
  };
  const unknown = !mode ? "mode" : roundValue !== null && !requested ? "review round" : null;
  return { task, round, setMode, unknown };
}

/** A review task's switch label: "Review · 12 left", or the round's name when
 * the caller has items in more than one ("Report check · 14 left"). */
export const taskLabel = (task: ReviewTask) => `${task.label} · ${task.left} left`;

/** The quiet switch between View and Review, e.g. labelled by `taskLabel`. Only
 * someone with a review task gets it. */
export function ReviewSwitch({
  label,
  on,
  onChange,
}: {
  label: string;
  on: boolean;
  onChange: (on: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={on}
      onClick={() => onChange(!on)}
      className="flex items-center gap-2 rounded-full border border-border px-2.5 py-0.5 text-[11.5px] text-fg-muted hover:text-fg focus-visible:outline focus-visible:outline-1 focus-visible:outline-signal aria-checked:border-border-strong aria-checked:text-fg"
    >
      <span className="tabular-nums">{label}</span>
      <span
        aria-hidden
        className={`relative h-3.5 w-6 rounded-full transition-colors motion-reduce:transition-none ${
          on ? "bg-signal" : "bg-surface-2"
        }`}
      >
        <span
          className={`absolute top-0.5 h-2.5 w-2.5 rounded-full bg-fg transition-transform motion-reduce:transition-none ${
            on ? "translate-x-3" : "translate-x-0.5"
          }`}
        />
      </span>
    </button>
  );
}
